/* eslint-disable */
import FirebaseProduct from './firebaseProduct.js';
import FirebaseProductParser from './firebaseProductParser.js';
import FirebaseProductCallbackUtil from './firebaseProductCallbackUtil.js';
import {ConfigService, LocalStorageUtil, nuskin} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import ShopContextService from '../shopContext.js';

/**
 *
 * FireBaseProductService
 *
 * @namespace FireBaseProductService
 */
let FireBaseProductService = function(){
    let PRODUCT_MARKET_STORAGE_KEY = 'products';
    let PRODUCT_DATA_STORED_VERSION = 'version';
    let MIN_SKU_LENGTH_FOR_SEARCH = 4;
    let MAX_SKU_LENGTH = 8;
    let MAX_MARKETS_TO_STORE = 2;
    let MIN_LOCAL_STORAGE_TO_MAINTAIN_BYTES = 20842;

    let loadedFromApache = false;
    let productDataInMemory;
    let synonyms;
    let isSynonymsBusy = false;
    nuskin.firebaseProductService = {};

    // ---------------------------------------------
    //
    //        Public Methods
    //
    // ---------------------------------------------
    var publicMethods = {

        /**
         * Returns the required endpoint headers for service calls
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.clearStoredProductData()
         */
        clearStoredProductData: function(){
            localStorage.removeItem(PRODUCT_MARKET_STORAGE_KEY);
        },

        /**
         * Returns the required endpoint headers for service calls
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.clearStoredProductDataForMarket(market)
         * @param {string} market
         */
        clearStoredProductDataForMarket: function(market){
            let productData = localStorage.getItem(PRODUCT_MARKET_STORAGE_KEY);
            if (productData){
                let product = JSON.parse(productData);
                if (product[market]) {
                    delete product[market];
                    localStorage.setItem(PRODUCT_MARKET_STORAGE_KEY, JSON.stringify(product));
                }
            }
        },

        /**
         * Read Firebase product data from Firebase and store the data into local storage
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.loadProducts(market, language, callBack)
         * @param {string} market
         */
        loadProducts: function(market, language, callBack){
            updateProductData(market, language, callBack);
        },

        /**
         * Return all firebase products for a market from local storage.
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.getProducts(market, language)
         * @returns {object} productList
         */
        getProducts: function(market, language) {
            return getFilteredProducts(market, language);
        },

        /**
         * Return all firebase product from local storage that match the search term, then call a callback function.
         * This method is used by the cart search drop down.
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.getProductsBySearchTerm(searchTerm, market, language, callbackFn)
         * @param {string} searchTerm
         */
        getProductsBySearchTerm: function(searchTerm, market, language, callbackFn) {
            let throwOutFreeProducts = true;
            let synonymCallback = function () {
                callbackFn({
                    success: true,
                    products: getFilteredProducts(market, language, searchTerm, throwOutFreeProducts)
                });
            };

            if (!isProductDataStoredForMarketAndLanguage(market, language)){
                let callBack = function(){
                    // todo Make sure the synonyms are loaded first.
                    insureSynonyms(synonymCallback);
                };
                updateProductData(market, language, callBack);
            }
            else{
                // todo Make sure the synonyms are loaded first.
                insureSynonyms(synonymCallback);
            }
        },

        /**
         * Return a firebase product for the given sku if it exists. This call may not give you anything if the firebase product data is not populated yet.
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.getProductBySku(productSku, market, language)
         * @returns {object} product
         */
        getProductBySku: function(productSku, market, language){
            let product;

            if (isProductDataStoredForMarketAndLanguage(market, language)) {
                try {
                    let products = JSON.parse(getStoredProductData());
                    let marketProducts = products[market];

                    if (marketProducts) {
                        let parser = new FirebaseProductParser(productSku, language, marketProducts[productSku]);
                        if (parser.doesLanguageExistForProduct(language) && parser.doesMarketDataAndPricingExistForProduct()) {
                            let user = UserService.getUser();
                            let priceType = ShopContextService.getPriceType(false, user); // used to set the price and formattedPrice on the products.

                            product = new FirebaseProduct(parser, priceType, market, user);
                        }
                    }
                }catch(e){
                    console.log("Exception thrown in getProductBySku -> " + e);
                }
            }

            return product;
        },

        /**
         * Get Product By Sku Using CB
         * @memberof FireBaseProductService
         * @example
         * FireBaseProductService.getProductBySkuUsingCB(productSku, market, language, callbackFn)
         * @param {string} productSku
         */
        getProductBySkuUsingCB: function(productSku, market, language, callbackFn){
            if (!isProductDataStoredForMarketAndLanguage(market, language)){
                let callBack = function(){
                    callbackFn(publicMethods.getProductBySku(productSku, market, language));
                };
                updateProductData(market, language, callBack);
            }
            else{
                callbackFn(publicMethods.getProductBySku(productSku, market, language));
            }
        },

        getLoadedFromApache: function() {
            return loadedFromApache;
        }
    };

    // ---------------------------------------------
    //
    //        Private Methods
    //
    // ---------------------------------------------
    // Return all firebase products from local storage that match the search term and market. If no search term is
    // specified we return all products for the market.
    function getFilteredProducts(market, language, searchTerm, throwOutFreeProducts) {
        let productList = [];
        if (isProductDataStoredForMarketAndLanguage(market, language)){
            try {
                let products = JSON.parse(getStoredProductData());
                var marketProducts = products[market];

                if (marketProducts) {
                    let user = nuskin.newAccount.UserService.getUser();
                    let priceType = ShopContextService.getPriceType(false, user); // priceType is used to set the price to be used on the products.
                    let product;
                    let parser;

                    // build up product objects for cart product list.
                    for (let productSku in marketProducts) {
                        // Make sure it is a sku and not the data version.
                        if (productSku !== PRODUCT_DATA_STORED_VERSION) {
                            parser = new FirebaseProductParser(productSku, language, marketProducts[productSku]);
                            if (parser.doesLanguageExistForProduct(language) && parser.doesMarketDataAndPricingExistForProduct()) {
                                product = new FirebaseProduct(parser, priceType, market, user);

                                // Only look at adding a product to the result if we are not throwing out free products.
                                if (!(throwOutFreeProducts && product.price === 0.00)){
                                    if (searchTerm){
                                        if (canDisplayProduct(product, searchTerm, language, user)){
                                            productList.push(product);
                                        }
                                    }
                                    else{
                                        productList.push(product);
                                    }
                                }
                            }
                        }
                    }
                }
            } catch(e){
                console.log("Exception thrown in getProductBySku -> " + e);
                productList = [];
            }
            if (productList.length == 0) {
                logNoProductsMatched(marketProducts.version, searchTerm, language);
            }
        }
        return sortProductsBasedOnWeightedSkus(productList);
    }

    function updateProductData(market, language, callBack){
        try {
            // Get the latest version for the data in Firebase.
            let FIREBASE_DATA_VERSION_URL = ConfigService.getMarketConfig().nsurl_firebase + "/version.json";
            let firebaseDataVersionUrl = FIREBASE_DATA_VERSION_URL.replace('{market}', market.toUpperCase());

            $.ajax({
                url: firebaseDataVersionUrl,
                type: "GET",
                crossDomain: true,
                async:false,
                success: function (version) {
                    if (version !== null) {
                        // If we don't have any firebase data in local storage we read the data from firebase and store it.
                        if (!isProductDataStoredForMarketAndLanguage(market, language)) {
                            writeFirebaseProducts(market, language, version, callBack);
                        }

                        // Else we need to make sure the version in firebase is the same version that we have stored locally.
                        else {
                            let products = JSON.parse(getStoredProductData());
                            let marketProducts = products[market];

                            if (marketProducts) {
                                let versionOfStoredData = marketProducts[PRODUCT_DATA_STORED_VERSION];

                                // If versions are not equal the stored data is to old.
                                if (version !== null && version !== versionOfStoredData) {
                                    let refreshPageCallback = function(){
                                        location.reload();
                                    };

                                    // Clear out the old data for the market.
                                    publicMethods.clearStoredProductDataForMarket(market);

                                    // Download and store the updated data.
                                    writeFirebaseProducts(market, language, version);
                                }
                            }

                            if (callBack) {
                                callBack();
                            }
                        }
                    }

                    // We can't read the Firebase product data version, Firebase might be down.
                    else{
                        // Try to load data from apache unless we have local storage or local memory.
                        console.log("Can't read Firebase product data. Attempting to update from Apache");

                        if (!isProductDataStoredForMarketAndLanguage(market, language)) {
                            updateProductDataFromApache(market, language, callBack);
                        }
                    }
                },
                error: function(xhr) {
                    // Firebase had a major error. Try to load data from apache unless we have local storage or local memory.
                    if (xhr) {
                        console.log("Could not get Firebase version number. Attempting to update from Apache:" + xhr.statusText);
                    }
                    else {
                        console.log("Could not get Firebase version number. Attempting to update from Apache.");
                    }

                    if (!isProductDataStoredForMarketAndLanguage(market, language)) {
                        updateProductDataFromApache(market, language, callBack);
                    }
                }
            });

        } catch (e) {
            return null;
        }
    }

    function updateProductDataFromApache(market, language, callBack){
        try {
            let APACHE_PRODUCT_DATA_URL = ConfigService.getMarketConfig().nsurl_apache;
            let apacheProductDataUrl = APACHE_PRODUCT_DATA_URL.replace('{market}', market.toUpperCase());
            let loadTime = Date.now();

            $.ajax({
                url: apacheProductDataUrl,
                type: "GET",
                crossDomain: true,
                async:false,
                success: function (productData) {
                    // If we don't have any product data in local storage or local memory we read the data from apache and store it.
                    loadTime = Date.now() - loadTime;
                    if (productData){
                        logProductLoadAnalytics(productData, loadTime, false);
                        writeProductDataToLocalStorage(market, language, productData, -1);
                        loadedFromApache = true;
                        if (callBack) {
                            callBack();
                        }
                    }
                },
                error: function(xhr) {
                    // Accessing Apache product data failed.
                    if (xhr){
                        console.log("Could not get Apache product data:"+xhr.statusText);
                    }
                    else {
                        console.log("Could not get Apache product data.");
                    }
                }
            });

        } catch (e) {
            return null;
        }
    }

    function isProductDataStoredForMarketAndLanguage(market, language){
        let dataStored = false;
        let localStorageProductData = localStorage.getItem(PRODUCT_MARKET_STORAGE_KEY);
        let productData;

        // Check to see if the data is stored for the market in local storage.
        if (localStorageProductData && localStorageProductData !== "{}"){
            productData = JSON.parse(localStorageProductData);

            // Check for the existence of the market and also sku's within the market.
            dataStored = productData[market] !== undefined && Object.keys(productData[market]).length > 1;
        }

        // Check to see if the data is stored for the market in memory.
        if (!dataStored && productDataInMemory){
            productData = JSON.parse(productDataInMemory);

            // Check for the existence of the market and also sku's within the market.
            dataStored = productData[market] !== undefined && Object.keys(productData[market]).length > 1;
        }

        // Check to see if the data stored for the market is for the correct language.
        if (dataStored){
            let productExistsForLanguage;

            for (let sku in productData[market]){
                productExistsForLanguage = productData[market][sku].languages[language] !== undefined;
                break;
            }

            dataStored = dataStored && productExistsForLanguage;
        }

        return dataStored;
    }

    function getStoredProductData(){
        let productData = localStorage.getItem(PRODUCT_MARKET_STORAGE_KEY);
        if ((!productData || productData === "{}") && productDataInMemory){
            productData = productDataInMemory;
        }
        return productData;
    }

    function writeFirebaseProducts(market, language, version, callbackFn) {
        // Only write the data if there is a version.
        if (version) {
            try {
                if (!isProductDataStoredForMarketAndLanguage(market, language)) {
                    FirebaseProductCallbackUtil.addCallbackForMarket(market, callbackFn);

                    if (!FirebaseProductCallbackUtil.isProductDataDownLoadInProcess(market)) {
                        FirebaseProductCallbackUtil.setProductDataDownloadInProcess(market, true);

                        let FIREBASE_DATA_FOR_VERSION_URL = ConfigService.getMarketConfig().nsurl_firebase + ".json?version={version}";
                        let firebaseUrl = FIREBASE_DATA_FOR_VERSION_URL.replace('{market}', market.toUpperCase()).replace('{version}', version);
                        let loadTime = Date.now();

                        $.ajax({
                            url: firebaseUrl,
                            type: "GET",
                            crossDomain: true,
                            success: function (productData) {
                                loadTime = Date.now() - loadTime;
                                logProductLoadAnalytics(productData, loadTime, true);
                                writeProductDataToLocalStorage(market, language, productData, version);

                                FirebaseProductCallbackUtil.setProductDataDownloadInProcess(market, false);
                            },
                            error: function (xhr) {
                                if (xhr){
                                    console.log("Could not get Firebase product data:" + xhr.statusText);
                                }
                                else {
                                    console.log("Could not get Firebase product data.");
                                }
                                FirebaseProductCallbackUtil.setProductDataDownloadInProcess(market, false);
                            }
                        });
                    }
                }
            } catch (e) {
                FirebaseProductCallbackUtil.setProductDataDownloadInProcess(market, false);
                return null;
            }
        }
    }

    function logProductLoadAnalytics(products, ajaxTime, fromFirebase) {
        if (ConfigService.getMarketConfig().enableProductDownloadTracking) {
            let productsString = JSON.stringify(products),
                metricName = fromFirebase ? "firebase" : "apache";

            nuskin.firebaseProductMetrics = {
                requestTime: ajaxTime,
                version: new Date(products.version).toLocaleString(),
                productsSize: LocalStorageUtil.getByteLength(productsString + PRODUCT_MARKET_STORAGE_KEY)
            };
            trackIt(metricName);
        }
    }

    function logLocalStorageWriteError(version, dataSize) {
        if (ConfigService.getMarketConfig().enableLocalstorageErrorTracking) {
            nuskin.firebaseProductMetrics = {
                version: new Date(version).toLocaleString(),
                productsSize: dataSize
            };
            trackIt('localstorageError');
        }
    }

    function logNoProductsMatched(version, searchTerm, language) {
        if (ConfigService.getMarketConfig().enableEmptySearchTracking) {
            nuskin.firebaseProductMetrics = {
                version: new Date(version).toLocaleString(),
                searchTerm: searchTerm,
                language: language
            };
            trackIt('noSearchResult');
        }
    }

    function trackIt(name) {
        if (typeof _satellite !== "undefined" && _satellite) {
            _satellite.track(name);
        }
    }

    function writeProductDataToLocalStorage(market, language, firebaseProductData, version){
        firebaseProductData = removeSuperfluousLanguageNodes(firebaseProductData, language);

        // Make sure that we will only have the maximum allowed amount of product data stored after the write.
        insureMaxMarketsStoredInLocalStorage(market);

        // Get all the storage data for all markets stored.
        let storageObject = getStoredProductData();

        if (storageObject){
            storageObject = JSON.parse(storageObject);
        }
        else{
            storageObject = {};
        }

        // Add the firebase data for a particular market.
        storageObject[market] = firebaseProductData;

        // Write the version for the data was written.
        storageObject[market][PRODUCT_DATA_STORED_VERSION] = version;

        // Try to write the products to local storage
        try {
            localStorage.setItem(PRODUCT_MARKET_STORAGE_KEY, JSON.stringify(storageObject));

            // Make sure we have enough local storage left for the cart to work. If we don't we need to delete the
            // products for all markets and try again.
            if (LocalStorageUtil.getRemainingLocalStorage() < MIN_LOCAL_STORAGE_TO_MAINTAIN_BYTES){
                publicMethods.clearStoredProductData();
                localStorage.setItem(PRODUCT_MARKET_STORAGE_KEY, JSON.stringify(storageObject));
            }
        }
        catch(e){
            // We don't have enough memory in local storage to write the Firebase products.
            try {
                // Sense there was an error, dump all the product data and try to write it again.
                publicMethods.clearStoredProductData();
                localStorage.setItem(PRODUCT_MARKET_STORAGE_KEY, JSON.stringify(storageObject));
            }
            catch(x){
                // We still can't write the data, store it as a variable and use it from there.
                console.log("Still can't write the data. Using local variable.");
                console.log(x);
                productDataInMemory = JSON.stringify(storageObject);
                logLocalStorageWriteError(firebaseProductData.version, LocalStorageUtil.getByteLength(productDataInMemory + PRODUCT_MARKET_STORAGE_KEY));
            }
        }

        // Make sure we have enough local storage left for the cart to work. If we don't we need to delete the
        // products for the market we just wrote from local storage and just use RAM as the product store.
        if (LocalStorageUtil.getRemainingLocalStorage() < MIN_LOCAL_STORAGE_TO_MAINTAIN_BYTES){
            publicMethods.clearStoredProductDataForMarket(market);

            console.log("Not enough local storage for cart and products. Using local variable.");
            productDataInMemory = JSON.stringify(storageObject);
        }
    }

    function canDisplayProduct(product, searchTerm, language, user){
        let canDisplay = false;

        if (product.available && product.searchable && product.lang === language && testMatchCustomerType(product, user)) {
            if (testMatchString(product.title, searchTerm) ||       // Match title
                testMatchString(product.shortDescr, searchTerm) ||  // Match short description
                testMatchSKU(product.sku, searchTerm)) {            // Match SKU
                canDisplay = true;
            }
        }
        return canDisplay;
    }

    function testMatchString(searchString, searchTerm){
        let match;

        // Check the string first for a match
        match = searchString.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;

        // If no match, try the synonyms.
        if (!match){
            let synonym = synonyms[searchTerm];
            if (synonym) {
                match = searchString.toLowerCase().indexOf(synonym.toLowerCase()) !== -1;
            }
        }

        return match;
    }

    function testMatchSKU(sku, searchTerm){
        return searchTerm.length >= MIN_SKU_LENGTH_FOR_SEARCH &&
            searchTerm.length <= MAX_SKU_LENGTH &&
            sku.indexOf(searchTerm) === 0;
    }

    function testMatchCustomerType(product, user){
        let match = false;

        // Here are the rules.
        // 1. Users with customer types of 20's or 30's are treated the same.
        // 2. If the user is not logged in, we show the product if it is not for distributors (10's) only;

        if (ConfigService.getMarketConfig().ignoreCustomerTypes){
            match = true;
        }
        else if (product.isCustomer() || product.isPreferredCustomer()){
            match = true;
        }
        else if (product.isDistributor()){
            if (user) {
                match = user.isDistributor();
            }
        }
        else{
            match = true;
        }

        return match;
    }

    function sortProductsBasedOnWeightedSkus(productList){
        let weightedSkuArray = ConfigService.getMarketConfig().weightedSkus;

        if (weightedSkuArray){
            let weightedProductsHashtable  = new $.Hashtable();
            let weightedProducts = [];
            let nonWeightedProducts = [];

            // Load the sku weighted test hashtable
            let skuWeightedHashtable = new $.Hashtable();
            for (let sortOrder=0;sortOrder<weightedSkuArray.length;sortOrder++){
                skuWeightedHashtable.add(weightedSkuArray[sortOrder], sortOrder);
            }

            // Sort between weighted and non weighted sku products.
            for (let i=0;i<productList.length;i++){
                if (skuWeightedHashtable.containsKey(productList[i].sku)){
                    // Stuff the products in a hashtable for sorting later on.
                    weightedProductsHashtable.add(productList[i].sku, productList[i]);
                }
                else{
                    nonWeightedProducts.push(productList[i]);
                }
            }

            // Sort the products based on how they are in the weighted sku array.
            let product;
            for (let j=0;j<weightedSkuArray.length;j++){
                product = weightedProductsHashtable.get(weightedSkuArray[j]);
                if (product){
                    weightedProducts.push(product);
                }
            }

            // Append the sorted product arrays.
            productList = [];
            productList = productList.concat(weightedProducts, nonWeightedProducts);
        }

        return productList;
    }

    function insureMaxMarketsStoredInLocalStorage(market){
        if (market){
            let productData = localStorage.getItem(PRODUCT_MARKET_STORAGE_KEY);
            if (productData && productData !== '{}') {
                let marketsInLocalStorage = Object.keys(JSON.parse(productData));

                if (marketsInLocalStorage.length === MAX_MARKETS_TO_STORE) {
                    // Delete the market key from "markets in local storage" array.
                    let indexOfMarket = marketsInLocalStorage.indexOf(market);

                    if (indexOfMarket === 0) {
                        publicMethods.clearStoredProductDataForMarket(marketsInLocalStorage[1]);
                    }
                    else {
                        publicMethods.clearStoredProductDataForMarket(marketsInLocalStorage[0]);
                    }
                }
            }
        }
    }

    function insureSynonyms(callback) {
        if (!isSynonymsBusy) {
            if (!synonyms) {
                isSynonymsBusy = true;
                let protocol = nuskin.util.Url.getProtocol(window.location.href);
                let host = nuskin.util.Url.getHost(window.location.href);

                let url = protocol + '//' + host + "/apps/products/services/synonyms";
                let promise = $.ajax({
                    url: url
                });

                promise.done(function (data) {
                    synonyms = data;
                    callback();
                    isSynonymsBusy = false;
                });
                promise.fail(function () {
                    if (typeof firebaseConfig !== "undefined") {
                        synonyms = firebaseConfig.synonyms;
                    }
                    isSynonymsBusy = false;
                });
            }
            else {
                callback();
            }
        }
    }

    function removeSuperfluousLanguageNodes(productData, language){
        let tempLanguagesNode;
        let tempLanguageNode;
        let tempPricing;
        let sku;
        let skusToBeRemoved = [];

        for (sku in productData) {
            if (sku !== 'version') {
                // Get the language node for the current selected language
                tempLanguagesNode = productData[sku].languages;
                tempLanguageNode = undefined;
                if (tempLanguagesNode !== undefined){
                    tempLanguageNode = productData[sku].languages[language];
                }

                tempPricing = productData[sku].priceMap;

                // We only store products in local storage if they have the language of interest and pricing.
                if (tempLanguageNode !== undefined && tempPricing !== undefined) {
                    productData[sku].languages = {};
                    productData[sku].languages[language] = tempLanguageNode;
                }
                else {
                    // Add this sku to the list of sku's to be removed from the product data.
                    skusToBeRemoved.push(sku);
                }
            }
        }

        // Remove all the skus that don't have the desired language
        for (let i=0; i< skusToBeRemoved.length; i++){
            delete productData[skusToBeRemoved[i]];
        }

        return productData;
    }

    return publicMethods;
}();

export default FireBaseProductService;
